import { Injectable, inject } from '@angular/core';
import { Observable, of, shareReplay, map, catchError } from 'rxjs';
import { AccountService } from '../account/account.service';
import { InterestProfile } from '../account-settings/models/interest-profiles.model';
import { UrlInterestProfileMap, createUrlInterestProfileMap } from './data-platform.utils';

/**
 * Service for loading and caching URL interest profiles.
 * URL interest profiles are used to translate URL dimension values (e.g., "/products/shoes")
 * to human-readable names (e.g., "Shoes").
 */
@Injectable({
  providedIn: 'root',
})
export class UrlInterestProfilesService {
  private readonly accountService = inject(AccountService);

  /**
   * Cached observable of interest profiles, replayed to all subscribers.
   * Null if not yet loaded, empty array if no profiles exist.
   */
  private profiles$: Observable<InterestProfile[] | null> | null = null;

  /**
   * Get a map from URL pattern (id) to human-readable name.
   * Loads profiles on first subscription and caches them.
   */
  getUrlInterestProfileMap(): Observable<UrlInterestProfileMap> {
    return this.getInterestProfiles().pipe(
      map((profiles) => createUrlInterestProfileMap(profiles ?? [])),
      catchError(() => of(new Map())),
    );
  }

  /**
   * Direct access to the interest profiles array.
   * Loads profiles on first subscription and caches them.
   */
  getInterestProfiles(): Observable<InterestProfile[] | null> {
    if (!this.profiles$) {
      this.profiles$ = this.loadInterestProfiles().pipe(shareReplay(1));
    }
    return this.profiles$;
  }

  /**
   * Reload the interest profiles (e.g., after creating/deleting profiles).
   */
  reload(): void {
    this.profiles$ = null;
  }

  private loadInterestProfiles(): Observable<InterestProfile[] | null> {
    return this.accountService.getInterestProfiles().pipe(catchError(() => of(null)));
  }
}
